import React, { useState } from "react";

import { toast } from "@/design-system/components/Toast/Toast";

import { invalidateQuery, useApiQuery } from "@/api";
import { RBACAccessLevel, RBACDomain } from "@/access-control/types";
import { publishConfigVersion } from "@/consent-management/api/publishConfigVersion";
import { updateConsentManagementConfigVersion } from "@/consent-management/api/updateConsentManagementConfigVersion";
import { ConsentManagerConfigSchema } from "@/consent-management/components/ConfigPage/ConsentManagerConfigForm/schema";
import { consentManagementIntegrations } from "@/consent-management/consentManagement";
import { useConsentCategorizableDestinations } from "@/consent-management/hooks/useConsentCategorizableDestinations";
import useListAllDestinationsForEnv from "@/destinations/v2/hooks/useListAllDestinationsForEnv";
import { Banner } from "@/design-system/components/Banner/Banner";
import { Button } from "@/design-system/components/Button/Button";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { Select } from "@/design-system/components/Select/Select";
import { Typography } from "@/design-system/components/Typography/Typography";
import useFeatureFlags from "@/hooks/useFeatureFlag";
import { useUserHasAccess } from "@/hooks/useUserHasAccess";
import { useHasSessionReplayHeatmapsEntitlement } from "@/sessionReplay/useHasSessionReplayHeatmapsEntitlement";
import { unpackRequestErrorMessage } from "@/utils/request";
import { WizardLink } from "@/wizards/framework/WizardLink";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import {
  hasOtherUnpublishedChanges,
  isConsentDraftPublishable,
  withConsentCategory,
} from "../consentDraft";
import { EnableDestinationStepProps } from "../stepProps";

export function ConsentCategoryStep({
  context,
  goNext,
}: EnableDestinationStepProps) {
  const { saasDestination: destination, activeConsentManager } = context;
  const consentType = activeConsentManager ?? "freshpaint";
  const integration = consentManagementIntegrations[consentType];
  const canWrite = useUserHasAccess(
    RBACDomain.consentManagement,
    RBACAccessLevel.write,
  );
  const { data, isLoading } = useApiQuery(
    ["get_consent_management_config_versions", { type: consentType }],
    { enabled: !!activeConsentManager },
  );
  const draft = data?.versions.find((version) => !version.publishedAt);
  const lastPublished = data?.versions.find((version) => version.publishedAt);
  const destinationKey = destination?.key ?? "";
  const savedCategory = draft?.consentMapping[destinationKey] ?? "";
  const [category, setCategory] = useState<string>("");
  const selectedCategory = category || savedCategory;
  const [saving, setSaving] = useState<"draft" | "publish" | null>(null);
  const enabledCategorizableDestinations =
    useConsentCategorizableDestinations();
  const destinationsQuery = useListAllDestinationsForEnv();
  const flag = useFeatureFlags();
  const sessionReplay = useHasSessionReplayHeatmapsEntitlement();

  const draftHasOtherChanges =
    !!draft &&
    hasOtherUnpublishedChanges({ draft, lastPublished, destinationKey });
  const publishCheckLoading =
    destinationsQuery.isLoading || sessionReplay.loading;
  const draftPublishable =
    !!draft &&
    !!selectedCategory &&
    !publishCheckLoading &&
    !destinationsQuery.isError &&
    isConsentDraftPublishable({
      draft: withConsentCategory(draft, destinationKey, selectedCategory),
      enabledCategorizableDestinations,
      audiencesEnabled: flag("audiences"),
      sessionReplayEntitled: sessionReplay.hasEntitlement,
    });

  async function save(publish: boolean) {
    if (!draft || !selectedCategory || (publish && !draftPublishable)) {
      return;
    }
    setSaving(publish ? "publish" : "draft");
    try {
      if (selectedCategory !== savedCategory) {
        await updateConsentManagementConfigVersion(
          withConsentCategory(
            draft,
            destinationKey,
            selectedCategory,
          ) as unknown as ConsentManagerConfigSchema,
        );
      }
      if (publish) {
        await publishConfigVersion({
          type: consentType,
          incrementRevision: false,
          publishDescription: `Categorized ${destination?.name} during destination setup`,
        });
        await invalidateQuery(["get_consent_management_configs", {}]);
      }
      goNext();
    } catch (error) {
      toast.error(unpackRequestErrorMessage(error));
      setSaving(null);
    }
  }

  const isPublished =
    !!lastPublished &&
    lastPublished.consentMapping[destinationKey] !== undefined;

  return (
    <WizardStepLayout
      title="Categorize for consent"
      description={`${integration.name} only sends events to ${destination?.name ?? "this destination"} when a visitor consents to its category.`}
      skippable
      secondaryActions={
        <Button
          variant="outline"
          onClick={() => void save(false)}
          loading={saving === "draft"}
          disabled={!selectedCategory || !!saving || canWrite === false}
        >
          Save draft only
        </Button>
      }
      primaryAction={{
        label: "Save and publish",
        onClick: () => void save(true),
        loading: saving === "publish",
        disabled:
          !selectedCategory ||
          !!saving ||
          canWrite === false ||
          !draftPublishable,
        disabledReason:
          canWrite === false
            ? "Ask an admin for permission to edit consent management"
            : !selectedCategory
              ? "Choose a category"
              : publishCheckLoading
                ? "Checking your consent draft"
                : "Your consent draft has errors to fix before publishing",
      }}
    >
      {isLoading ? (
        <LoadingSpinner className="size-6" />
      ) : !draft ? (
        <Banner type="warning">
          We couldn’t find a draft for {integration.name}. Categorize{" "}
          {destination?.name} on the{" "}
          <WizardLink
            to={integration.configPageUrl()}
            className="cursor-pointer underline"
          >
            consent management page
          </WizardLink>
          .
        </Banner>
      ) : (
        <>
          <Select
            options={Object.entries(draft.categoryMapping)}
            getOptionLabel={([, label]) => label}
            getOptionValue={([id]) => id}
            rootProps={{
              value: selectedCategory,
              onValueChange: setCategory,
            }}
            triggerProps={{
              placeholder: "Select a category",
              "aria-label": "Consent category",
            }}
          />
          {isPublished && selectedCategory === savedCategory && (
            <Typography variant="body-sm" color="subtle">
              {destination?.name} is already categorized in the published
              version.
            </Typography>
          )}
          {selectedCategory && !publishCheckLoading && !draftPublishable && (
            <Banner type="warning">
              Your {integration.name} consent draft has errors, so it can’t be
              published from here. Save this category to the draft, then fix and
              publish it on the{" "}
              <WizardLink
                to={integration.configPageUrl()}
                className="cursor-pointer underline"
              >
                consent management page
              </WizardLink>
              .
            </Banner>
          )}
          {draftHasOtherChanges && (
            <Banner type="warning">
              Your {integration.name} consent draft has other unpublished
              changes. Publishing here publishes them too.{" "}
              <WizardLink
                to={integration.configPageUrl()}
                className="cursor-pointer underline"
              >
                Review the draft
              </WizardLink>
            </Banner>
          )}
        </>
      )}
    </WizardStepLayout>
  );
}
